/**
 * useDismissOnboarding — POST /users/me/dismiss-onboarding (204).
 *
 * Idempotent server-side. Optimistically flips the cached `onboarding_dismissed`
 * flag on the `auth.me` query so the coachmark banner unmounts immediately;
 * on error, reverts the cache and leaves the banner visible (the user can
 * try again next time the page loads).
 *
 * Phase 2.1 (shell-spec §5.2). Backend ships in a parallel worktree under
 * the same field name (`onboarding_dismissed`). Until backend lands, this
 * call will 404 — the catch keeps the UI quiet (banner stays up) without
 * surfacing a toast for a feature the user didn't trigger.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import type { Me } from "@/hooks/queries/auth/useMe";

const ME_KEY = ["auth", "me"] as const;

export function useDismissOnboarding() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void, { previous: Me | undefined }>({
    mutationFn: async () => {
      await apiClient.post("/users/me/dismiss-onboarding");
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ME_KEY });
      const previous = queryClient.getQueryData<Me>(ME_KEY);
      if (previous) {
        queryClient.setQueryData<Me>(ME_KEY, {
          ...previous,
          onboarding_dismissed: true,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Backend not yet shipped: revert so the next page load still shows
      // the banner (no silent permanent dismiss). No toast — user didn't
      // initiate this directly enough to merit a failure surface.
      if (context?.previous) {
        queryClient.setQueryData<Me>(ME_KEY, context.previous);
      }
    },
  });
}
